/**
 * Copyright (c) 2019-Present Syncari - All rights reserved.
 */
import cx from 'classnames';
import * as React from 'react';
import { FloatingPortal, autoUpdate, flip, offset, size as floatingSize, useFloating } from '@floating-ui/react';

import type { EmailInputProps } from '../../types';
import Icon from '../Icon/Icon';
import './EmailInput.less';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const isValidEmail = (val: string) => EMAIL_REGEX.test(val);

// Token inputs (containing `{{`) may have spaces inside the token, so only
// split on comma/semicolon. Plain email addresses may also be separated by space.
const getSeparators = (val: string): string[] => (val.includes('{{') ? [',', ';'] : [',', ';', ' ']);

const EmailInput = React.forwardRef<HTMLDivElement, EmailInputProps>(
  (
    {
      value,
      defaultValue,
      onChange,
      disabled = false,
      placeholder,
      size = 'medium',
      invalid = false,
      id,
      name,
      'aria-label': ariaLabel,
      className,
    },
    ref
  ) => {
    const isControlled = value !== undefined;
    const [internalEmails, setInternalEmails] = React.useState<string[]>(defaultValue ?? []);
    const [inputValue, setInputValue] = React.useState('');
    const [isFocused, setIsFocused] = React.useState(false);
    const [inputInvalid, setInputInvalid] = React.useState(false);
    const [enteringIdx, setEnteringIdx] = React.useState<number | null>(null);
    const inputRef = React.useRef<HTMLInputElement>(null);
    const shellRef = React.useRef<HTMLDivElement | null>(null);

    const emails = isControlled ? value ?? [] : internalEmails;
    const suggestion = inputValue.trim();
    const showDropdown = isFocused && (emails.length > 0 || suggestion.length > 0);

    const { refs, floatingStyles } = useFloating({
      placement: 'bottom-start',
      strategy: 'fixed',
      middleware: [
        offset(4),
        flip(),
        floatingSize({
          apply({ rects, elements }) {
            Object.assign(elements.floating.style, { width: `${rects.reference.width}px` });
          },
        }),
      ],
      whileElementsMounted: autoUpdate,
    });

    const updateEmails = (next: string[]) => {
      if (!isControlled) setInternalEmails(next);
      onChange?.(next);
    };

    const addEmail = (val: string) => {
      const trimmed = val.trim();
      if (!trimmed) return;
      if (!isValidEmail(trimmed)) {
        setInputInvalid(true);
        return;
      }
      if (emails.includes(trimmed)) {
        setInputValue('');
        return;
      }
      const next = [...emails, trimmed];
      setEnteringIdx(next.length - 1);
      updateEmails(next);
      setInputValue('');
      setInputInvalid(false);
    };

    const removeEmail = (idx: number) => {
      updateEmails(emails.filter((_, i) => i !== idx));
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      setInputInvalid(false);
      const lastChar = val[val.length - 1];
      if (getSeparators(val).includes(lastChar)) {
        addEmail(val.slice(0, -1));
      } else {
        setInputValue(val);
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        addEmail(inputValue);
      } else if (e.key === 'Backspace' && inputValue === '') {
        removeEmail(emails.length - 1);
      }
    };

    const handleBlur = () => {
      setIsFocused(false);
      if (inputValue.trim()) {
        addEmail(inputValue);
      }
    };

    const handleShellClick = (e: React.MouseEvent<HTMLDivElement>) => {
      if ((e.target as HTMLElement).closest('button')) return;
      inputRef.current?.focus();
    };

    const handleSuggestionMouseDown = (e: React.MouseEvent) => {
      e.preventDefault();
      addEmail(inputValue);
      inputRef.current?.focus();
    };

    const handleExistingMouseDown = (e: React.MouseEvent, idx: number) => {
      e.preventDefault();
      removeEmail(idx);
      inputRef.current?.focus();
    };

    React.useEffect(() => {
      if (enteringIdx === null) return;
      const frameId = requestAnimationFrame(() => setEnteringIdx(null));
      return () => cancelAnimationFrame(frameId);
    }, [enteringIdx]);

    const setShellRef = React.useCallback(
      (node: HTMLDivElement | null) => {
        shellRef.current = node;
        refs.setReference(node);
        if (typeof ref === 'function') ref(node);
        else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
      },
      [ref, refs]
    );

    return (
      <div
        ref={setShellRef}
        className={cx(
          'ds-email-input',
          `ds-email-input--${size}`,
          {
            'ds-email-input--focused': isFocused,
            'ds-email-input--disabled': disabled,
            'ds-email-input--invalid': invalid || inputInvalid,
          },
          className
        )}
        onClick={handleShellClick}>
        {emails.map((email, idx) => (
          <span
            key={email}
            className={cx('ds-email-input__pill', {
              'ds-email-input__pill--entering': idx === enteringIdx,
            })}>
            <span className="ds-email-input__pill-label">{email}</span>
            <button
              type="button"
              className="ds-email-input__pill-remove"
              aria-label={`Remove ${email}`}
              disabled={disabled}
              onClick={() => removeEmail(idx)}>
              <Icon name="actionable/close" size="small" />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          id={id}
          name={name}
          aria-label={ariaLabel}
          className="ds-email-input__input"
          type="text"
          value={inputValue}
          disabled={disabled}
          placeholder={emails.length === 0 ? placeholder : undefined}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsFocused(true)}
          onBlur={handleBlur}
        />
        {showDropdown && (
          <FloatingPortal>
            <div ref={refs.setFloating} className="ds-email-input__dropdown" style={floatingStyles}>
              {emails.map((email, idx) => (
                <div
                  key={email}
                  className="ds-email-input__dropdown-option ds-email-input__dropdown-option--selected"
                  onMouseDown={(e) => handleExistingMouseDown(e, idx)}>
                  <span className="ds-email-input__dropdown-option-label">{email}</span>
                  <Icon name="actionable/tick" size="small" className="ds-email-input__dropdown-check" />
                </div>
              ))}
              {suggestion && !emails.includes(suggestion) && (
                <div className="ds-email-input__dropdown-option" onMouseDown={handleSuggestionMouseDown}>
                  <span className="ds-email-input__dropdown-option-label">{suggestion}</span>
                </div>
              )}
            </div>
          </FloatingPortal>
        )}
      </div>
    );
  }
);

EmailInput.displayName = 'EmailInput';

export default EmailInput;
